import React from "react";
import type { UserListItem } from "types/domain";
import { useLanguage } from "context/LanguageContext";
import ParticipantUserAvatar from "./ParticipantUserAvatar";

interface SelectedParticipantCardProps {
  user: UserListItem;
  label: string;
  formatDob: (value?: string | null) => string;
  variant?: "primary" | "neutral";
  helperText?: string | null;
}

const SelectedParticipantCard = ({
  user,
  label,
  formatDob,
  variant = "neutral",
  helperText,
}: SelectedParticipantCardProps): JSX.Element => {
  const { t } = useLanguage();
  const toneClass =
    variant === "primary"
      ? "border-brand-300 bg-brand-50 dark:border-brand-400/50 dark:bg-brand-900/10"
      : "border-line bg-white dark:bg-surface-muted/50";

  const badgeClass =
    variant === "primary"
      ? "bg-brand-500 text-white"
      : "bg-surface-muted text-gray-600 dark:text-gray-300";

  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-2xl border p-3 ${toneClass}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <ParticipantUserAvatar user={user} size="md" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">
            {user.full_name}
          </p>
          <p className="truncate text-xs text-ink-muted">
            {user.email}
          </p>
          <p className="mt-1 text-xs text-ink-muted">
            {t("sessionEditor.dobLine", { dob: formatDob(user.dob) })}
          </p>

          {helperText ? (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-300">
              {helperText}
            </p>
          ) : null}
        </div>
      </div>
      <span
        className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}
      >
        {label}
      </span>
    </div>
  );
};

export default SelectedParticipantCard;
